import { RootState } from '@/store/types';
import { Project, ProjectState } from '@/store/modules/project/types';
import { Module, GetterTree, MutationTree, ActionTree } from 'vuex';
import { projectAPI, CreateProjectForm, UpdateProjectForm } from '@/api/project';
import { ElMessage } from 'element-plus';

export const moduleName = "project";

export enum ProjectGetters {
    getProjectList = "GET_PROJECT_LIST",
    getOwnProjectList = "GET_OWN_PROJECT_LIST",
    getLoading = "GET_LOADING",
    getProjectGroup = "GET_PROJECT_GROUP",
}

export enum ProjectMutations {
    setProjectList = "SET_PROJECT_LIST",
    setOwnProjectList = "SET_OWN_PROJECT_LIST",
    setLoading = "SET_LOADING",
    setProjectGroup = "SET_PROJECT_GROUP",
    resetAll = "RESET_ALL",
}

export enum ProjectActions {
    asyncGetProjectList = "ASYNC_GET_PROJECT_LIST",
    asyncGetOwnProjectList = "ASYNC_GET_OWN_PROJECT_LIST",
    asyncCreateProject = "ASYNC_CREATE_PROJECT",
    asyncDeleteProject = "ASYNC_DELETE_PROJECT",
    asyncUpdateProject = "ASYNC_UPDATE_PROJECT",
    asyncGetPprojectById = "ASYNC_GET_PROJECT_BY_ID"
}

const state: ProjectState = {
    projects: [],
    ownProjects: [],
    isLoading: false,
    groups: [],
}

export const getters: GetterTree<ProjectState, RootState> = {
    [ProjectGetters.getProjectList](state): Project[] {
        return state.projects;
    },
    [ProjectGetters.getOwnProjectList](state): Project[] {
        return state.ownProjects;
    },
    [ProjectGetters.getLoading](state): boolean {
        return state.isLoading;
    },
    [ProjectGetters.getProjectGroup](state): string[] {
        return state.groups;
    },
}

const mutations: MutationTree<ProjectState> = {
    [ProjectMutations.setProjectList](state, payload: Project[]): void {
        state.projects = payload;
    },
    [ProjectMutations.setOwnProjectList](state, payload: Project[]): void {
        state.ownProjects = payload;
    },
    [ProjectMutations.setLoading](state, payload: boolean): void {
        state.isLoading = payload;
    },
    [ProjectMutations.setProjectGroup](state, payload: string[]): void {
        state.groups = payload;
    },
    [ProjectMutations.resetAll](state) {
        Object.assign(state, {
            projects: [],
            ownProjects: [],
            isLoading: false,
            groups: [],
        })
    },
}

const actions: ActionTree<ProjectState, RootState> = {
    async [ProjectActions.asyncGetProjectList]({ commit }): Promise<void> {
        try {
            commit(ProjectMutations.setLoading, true);
            const projectList = await projectAPI.getProjectList();
            let groupList: string[] = []
            for (let i = 0; i < projectList.length; i++) {
                if (projectList[i].group !== '') {
                    const groups = projectList[i].group.split(",")
                    groupList = groupList.concat(groups.filter(item => !groupList.includes(item)))
                }
            }
            commit(ProjectMutations.setProjectGroup, groupList);
            commit(ProjectMutations.setProjectList, projectList);
        } catch (err) {
            console.log(err);
        } finally {
            commit(ProjectMutations.setLoading, false);
        }
    },
    async [ProjectActions.asyncGetOwnProjectList]({ commit }): Promise<void> {
        try {
            commit(ProjectMutations.setLoading, true);
            const projectList = await projectAPI.getOwnProjectList();
            commit(ProjectMutations.setOwnProjectList, projectList);
        } catch (err) {
            console.log(err);
        } finally {
            commit(ProjectMutations.setLoading, false);
        }
    },
    async [ProjectActions.asyncCreateProject]({ commit }, createProjectMsg: CreateProjectForm): Promise<boolean> {
        try {
            commit(ProjectMutations.setLoading, true);
            const res = await projectAPI.createProject(createProjectMsg);
            ElMessage({
                message: '创建项目成功',
                type: 'success',
            })
            const projectList = await projectAPI.getProjectList();
            let groupList: string[] = []
            for (let i = 0; i < projectList.length; i++) {
                if (projectList[i].group !== '') {
                    const groups = projectList[i].group.split(",")
                    groupList = groupList.concat(groups.filter(item => !groupList.includes(item)))
                }
            }
            commit(ProjectMutations.setProjectGroup, groupList);

            commit(ProjectMutations.setProjectList, projectList);
            return true
        } catch (err) {
            console.log(err)
            return false
        } finally {
            commit(ProjectMutations.setLoading, false);
        }
    },
    async [ProjectActions.asyncDeleteProject]({ commit }, id: string): Promise<void> {
        try {
            commit(ProjectMutations.setLoading, true);
            const res = await projectAPI.deleteProject(id);
            const projectList = await projectAPI.getProjectList();
            let groupList: string[] = []
            for (let i = 0; i < projectList.length; i++) {
                if (projectList[i].group !== '') {
                    const groups = projectList[i].group.split(",")
                    groupList = groupList.concat(groups.filter(item => !groupList.includes(item)))
                }
            }
            commit(ProjectMutations.setProjectGroup, groupList);

            commit(ProjectMutations.setProjectList, projectList);
        } catch (err) {
            console.log(err)
        } finally {
            commit(ProjectMutations.setLoading, false);
        }
    },
    async [ProjectActions.asyncUpdateProject]({ commit }, updateProjectMsg: UpdateProjectForm): Promise<boolean> {
        try {
            commit(ProjectMutations.setLoading, true);
            const res = await projectAPI.updateProject(updateProjectMsg);
            ElMessage({
                message: '编辑项目成功',
                type: 'success',
            })
            const projectList = await projectAPI.getProjectList();
            let groupList: string[] = []
            for (let i = 0; i < projectList.length; i++) {
                if (projectList[i].group !== '') {
                    const groups = projectList[i].group.split(",")
                    groupList = groupList.concat(groups.filter(item => !groupList.includes(item)))
                }
            }
            commit(ProjectMutations.setProjectGroup, groupList);

            commit(ProjectMutations.setProjectList, projectList);
            return true
        } catch (err) {
            console.log(err)
            return false
        } finally {
            commit(ProjectMutations.setLoading, false);
        }
    },
}

export const project: Module<ProjectState, RootState> = {
    namespaced: true,
    state,
    actions,
    getters,
    mutations,
}
